import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LeadboxSidebar } from "./LeadboxSidebar";
import { cn } from "@/lib/utils";
import {
  flattenLeadboxNavForStrip,
  getLeadboxNavSections,
  isLeadboxNavLinkActive,
} from "./leadboxNavModel";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useShowNavigateBack } from "@/hooks/useShowNavigateBack";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { getLanguage } from "@/utils/languageUtils";
import { commonButtonLabels } from "@/i18n";
import { useCurrentAdminPermissions } from "@/hooks/useCurrentAdminPermissions";

interface LeadboxLayoutProps {
  children: React.ReactNode;
  /** Set when the page renders its own back control (detail views). */
  hideLayoutBack?: boolean;
}

/** Detail routes that provide their own back button. */
function leadboxPageHasOwnBack(pathname: string): boolean {
  return (
    /^\/admin\/leads\/[^/]+$/.test(pathname) ||
    /^\/admin\/leadbox\/vehicle-cards\/[^/]+$/.test(pathname) ||
    /^\/admin\/support\/[^/]+$/.test(pathname)
  );
}

export const LeadboxLayout = ({
  children,
  hideLayoutBack = false,
}: LeadboxLayoutProps) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem("sidebar-collapsed");
    return stored ? JSON.parse(stored) : true;
  });
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const showPageBack = useShowNavigateBack();
  const location = useLocation();
  const { canViewBidsOverview } = useCurrentAdminPermissions();
  const mobileStripLinks = flattenLeadboxNavForStrip(
    getLeadboxNavSections({ canViewBidsOverview }),
  );
  const showLayoutBack =
    showPageBack &&
    !hideLayoutBack &&
    !leadboxPageHasOwnBack(location.pathname);

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background">
      {isMobile && (
        <nav
          className="w-full min-w-0 border-b border-border bg-muted/20 md:hidden"
          aria-label="Leadbox"
        >
          <div className="flex w-full min-w-0 gap-1 overflow-x-auto overflow-y-hidden px-1.5 py-1.5 touch-pan-x md:gap-1.5 md:px-2 md:py-2">
            {mobileStripLinks.map((item) => (
              <Link
                key={item.url}
                to={item.url}
                className={cn(
                  "flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-2.5 text-xs font-semibold transition-colors md:gap-2 md:px-3.5 md:py-2.5 md:text-sm",
                  isLeadboxNavLinkActive(item.url, location.pathname)
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border/60 bg-background text-foreground shadow-sm",
                )}
              >
                <item.icon className="h-4 w-4 shrink-0 md:h-4 md:w-4" />
                <span className="whitespace-nowrap">{item.title}</span>
              </Link>
            ))}
          </div>
        </nav>
      )}
      {!isMobile && (
        <LeadboxSidebar
          isCollapsed={isCollapsed}
          setIsCollapsed={(collapsed) => {
            setIsCollapsed(collapsed);
            if (typeof window !== "undefined" && window.innerWidth >= 768) {
              localStorage.setItem(
                "sidebar-collapsed",
                JSON.stringify(collapsed),
              );
            }
          }}
        />
      )}
      <main className="flex-1 min-w-0 min-h-screen w-full">
        <div
          className={cn(
            "container mx-auto p-2 md:py-4 md:pl-2 md:pr-4 space-y-4 md:space-y-6",
            isNative && "native-leadbox-page",
          )}
        >
          {showLayoutBack && (
            <BtnNavigateBack
              label={commonButtonLabels(getLanguage()).back}
              to="/dashboard"
            />
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

export default LeadboxLayout;
